// src/pages/SearchEngine.tsx
import React, { useState, useEffect } from "react";

// --- Types ---
interface FilterCriteria {
  country: string;
  school: string;
  yla: string;
  login: string;
  surname: string;
  phone: string;
  zipcode: string;
  email: string;
  suburb: string;
  dongle: string;
  student: string;
}

interface YLARecord {
  Distributor: string;
  FamilyCode: string;
  Login: string;
  Surname: string;
  Address: string;
  Suburb: string;
  State: string;
  Phone: string;
  Email: string;
  PostCode: string;
  DateOfSale: string;
  Country: string;
}

interface ELearnWebRecord {
  SchoolCode: string;
  FamilyCode: string;
  Distributor: string;
  Login: string;
  Surname: string;
  Address: string;
  Suburb: string;
  State: string;
  Phone: string;
  Email: string;
  PostCode: string;
  DateOfSale: string;
  HardLocks: string;
  OrderRef: string;
}


const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const fetchCountries = async (): Promise<string[]> => {
  const response = await fetch(`${API_BASE_URL}api/suse/countries/`);
  if (!response.ok) throw new Error("Failed to fetch countries");
  const data = await response.json();
  return data.countries;
};

const fetchSchools = async (country: string): Promise<{ name: string; code: string }[]> => {
  const response = await fetch(`${API_BASE_URL}api/suse/schools/?country=${country}`);
  if (!response.ok) throw new Error("Failed to fetch schools");
  const data = await response.json();
  return data.schools;
};

const fetchYLADistributors = async (country: string): Promise<string[]> => {
  const response = await fetch(`${API_BASE_URL}api/suse/yla-distributors/?country=${country}`);
  if (!response.ok) throw new Error("Failed to fetch YLA distributors");
  const data = await response.json();
  return data.distributors;
};

const searchYLADatabase = async (filters: FilterCriteria): Promise<YLARecord[]> => {
  const response = await fetch(`${API_BASE_URL}api/suse/search-yla/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filters }),
  });
  if (!response.ok) throw new Error("Failed to search YLA database");
  const data = await response.json();
  return data.results;
};

const searchELearnWebDatabase = async (filters: FilterCriteria): Promise<ELearnWebRecord[]> => {
  const response = await fetch(`${API_BASE_URL}api/suse/search-elearnweb/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filters }),
  });
  if (!response.ok) throw new Error("Failed to search eLearnWeb database");
  const data = await response.json();
  return data.results;
};

// --- Component ---
const SearchEngine: React.FC = () => {
  // State for filters
  const [filters, setFilters] = useState<FilterCriteria>({
    country: "Australia",
    school: "",
    yla: "",
    login: "",
    surname: "",
    phone: "",
    zipcode: "",
    email: "",
    suburb: "",
    dongle: "",
    student: "",
  });

  // State for dropdowns
  const [countries, setCountries] = useState<string[]>([]);
  const [schools, setSchools] = useState<{ name: string; code: string }[]>([]);
  const [ylaDistributors, setYLADistributors] = useState<string[]>([]);

  // State for results
  const [ylaResults, setYLAResults] = useState<YLARecord[]>([]);
  const [elearnWebResults, setELearnWebResults] = useState<ELearnWebRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countriesData = await fetchCountries();
        setCountries(countriesData);
      } catch (err) {
        setError("Failed to load initial data");
      }
    };
    loadInitialData();
  }, []);

  // Fetch schools and YLA distributors when country changes
  useEffect(() => {
    const loadDependentData = async () => {
      if (filters.country) {
        try {
          const [schoolsData, ylaData] = await Promise.all([
            fetchSchools(filters.country),
            fetchYLADistributors(filters.country),
          ]);
          setSchools(schoolsData);
          setYLADistributors(ylaData);
        } catch (err) {
          setError("Failed to load dependent data");
        }
      }
    };
    loadDependentData();
  }, [filters.country]);

  // Handle filter changes
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Handle search
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate at least one filter is provided
    const hasFilter = Object.values(filters).some((val) => val.trim() !== "");
    if (!hasFilter) {
      setError("Please enter some search criteria.");
      setIsLoading(false);
      return;
    }

    try {
      const [ylaData, elearnData] = await Promise.all([
        searchYLADatabase(filters),
        searchELearnWebDatabase(filters),
      ]);
      setYLAResults(ylaData);
      setELearnWebResults(elearnData);
    } catch (err) {
      setError("Failed to fetch search results");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset
  const handleReset = () => {
    setFilters({
      country: "Australia",
      school: "",
      yla: "",
      login: "",
      surname: "",
      phone: "",
      zipcode: "",
      email: "",
      suburb: "",
      dongle: "",
      student: "",
    });
    setYLAResults([]);
    setELearnWebResults([]);
    setError(null);
  };

  // Handle numeric input (for phone, dongle, zipcode)
  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "Backspace", "Delete", "Tab"];
    if (!allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  // --- Render ---
  return (
    <div style={{ margin: 0, backgroundColor: "azure", minHeight: "100vh", padding: "20px" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <h1 style={{ color: "red", fontFamily: "Arial", fontSize: "16pt" }}>Search Engine</h1>
        <button
          onClick={() => window.history.back()}
          style={{
            width: "100px",
            backgroundColor: "#f0f0f0",
            border: "1px solid #ccc",
            padding: "5px",
            cursor: "pointer",
          }}
        >
          Back
        </button>
      </div>

      {/* Filters Form */}
      <form onSubmit={handleSearch} style={{ marginBottom: "20px" }}>
        <table
          style={{
            fontFamily: "Arial",
            fontSize: "11pt",
            width: "99%",
            borderCollapse: "collapse",
            border: "1px solid black",
          }}
        >
          <tbody>
            {/* Row 1 */}
            <tr>
              <td style={{ border: "1px solid black", padding: "5px", width: "150px" }}>Country:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <select
                  name="country"
                  value={filters.country}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                >
                  <option value="">-- Select --</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: "1px solid black", padding: "5px", width: "140px" }}>YLA:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <select
                  name="yla"
                  value={filters.yla}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                >
                  <option value="">-- Select --</option>
                  {ylaDistributors.map((distributor) => (
                    <option key={distributor} value={distributor}>
                      {distributor}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: "1px solid black", padding: "5px", width: "120px" }}>Login:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="login"
                  value={filters.login}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                />
              </td>
              <td style={{ border: "1px solid black", padding: "5px", width: "120px" }}>Surname:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="surname"
                  value={filters.surname}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                />
              </td>
              <td style={{ border: "1px solid black", padding: "5px", width: "100px" }}>Ph No:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="phone"
                  value={filters.phone}
                  onChange={handleFilterChange}
                  onKeyDown={handleNumericInput}
                  maxLength={10}
                  style={{ width: "120px" }}
                />
              </td>
              <td style={{ border: "1px solid black", padding: "5px", width: "140px" }}>Suburb:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="suburb"
                  value={filters.suburb}
                  onChange={handleFilterChange}
                  style={{ width: "100px" }}
                />
              </td>
            </tr>
            {/* Row 2 */}
            <tr>
              <td style={{ border: "1px solid black", padding: "5px" }}>Dongle Number:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="dongle"
                  value={filters.dongle}
                  onChange={handleFilterChange}
                  onKeyDown={handleNumericInput}
                  maxLength={9}
                  style={{ width: "120px" }}
                />
              </td>
              <td style={{ border: "1px solid black", padding: "5px" }}>eLearnWeb:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <select
                  name="school"
                  value={filters.school}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                >
                  <option value="">-- Select --</option>
                  {schools.map((school) => (
                    <option key={school.code} value={school.code}>
                      {school.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: "1px solid black", padding: "5px" }}>Student:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="student"
                  value={filters.student}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                />
              </td>
              <td style={{ border: "1px solid black", padding: "5px" }}>Zipcode:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="zipcode"
                  value={filters.zipcode}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                />
              </td>
              <td style={{ border: "1px solid black", padding: "5px" }}>E-Mail:</td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                <input
                  type="text"
                  name="email"
                  value={filters.email}
                  onChange={handleFilterChange}
                  style={{ width: "120px" }}
                />
              </td>
              <td colSpan={2} style={{ border: "1px solid black", padding: "5px", textAlign: "center" }}>
                <button
                  type="submit"
                  style={{
                    width: "75px",
                    backgroundColor: "#f0f0f0",
                    border: "1px solid #ccc",
                    padding: "5px",
                    cursor: "pointer",
                    marginRight: "10px",
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? "Searching..." : "GO"}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    width: "75px",
                    backgroundColor: "#f0f0f0",
                    border: "1px solid #ccc",
                    padding: "5px",
                    cursor: "pointer",
                  }}
                >
                  Reset
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {/* Error Message */}
      {error && (
        <div style={{ color: "red", marginBottom: "10px", textAlign: "center" }}>{error}</div>
      )}

      {/* YLA Database Results */}
      {ylaResults.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <h2 style={{ color: "white", backgroundColor: "teal", padding: "10px", textAlign: "center" }}>
            Details from NEW Database for Bootcamp customers only. Select a row to go to new YLA support.
          </h2>
          <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #ccc" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Arial",
                fontSize: "11pt",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f2f2f2" }}>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Distributor</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Family Code</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Login</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Surname</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Address</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Suburb</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>State</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Phone</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Email</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>PostCode</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Date of Sale</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Country</th>
                </tr>
              </thead>
              <tbody>
                {ylaResults.map((row, index) => (
                  <tr
                    key={index}
                    onClick={() => {
                      window.open(
                        `http://localhost:70/CustomerSupport_View_a.asp?hidMigration=Migration&hidFamilyCode=${row.FamilyCode}&hidSCButton=1`,
                        "YLA Support",
                        ""
                      );
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Distributor}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.FamilyCode}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Login}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Surname}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Address}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Suburb}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.State}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Phone}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Email}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.PostCode}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.DateOfSale}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* eLearnWeb Database Results */}
      {elearnWebResults.length > 0 && (
        <div>
          <h2 style={{ color: "white", backgroundColor: "teal", padding: "10px", textAlign: "center" }}>
            Details from OLD Database
          </h2>
          <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #ccc" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Arial",
                fontSize: "11pt",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f2f2f2" }}>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>School Code</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Family Code</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Distributor</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Login</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Surname</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Address</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Suburb</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>State</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Phone</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Email</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>PostCode</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Date of Sale</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>HardLocks</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>OrderRef</th>
                </tr>
              </thead>
              <tbody>
                {elearnWebResults.map((row, index) => (
                  <tr
                    key={index}
                    onClick={() => {
                      const url = `/SUPPORT/EL_AddEditTask.aspx?SchoolCode=${row.SchoolCode}&FamilyCode=${row.FamilyCode}&Login=${row.Login}&S=A&F=AA&L=admin`;
                      window.open(url, "_blank");
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.SchoolCode}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.FamilyCode}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Distributor}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Login}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Surname}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Address}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Suburb}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.State}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Phone}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.Email}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.PostCode}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.DateOfSale}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.HardLocks}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{row.OrderRef}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchEngine;